import type { ClientHttpConnector } from '../../client/types.js';
import { MockClientHttpRequest, MockClientHttpResponse } from '../../mock/client/exchange.ts';
import { MockServerHttpRequest, MockServerHttpResponse } from '../../mock/server/exchange.ts';
import { BodyChunk, HttpMethod } from '../../../types/web/http';
import type { ClientHttpRequest, ClientHttpResponse } from '../../../types/web/client';

import getLogger from '../../logger.js';
import type { ServerHttpRequest, ServerHttpResponse, SslInfo } from '../../../types/web/server';
import { HttpHeadResponseDecorator } from '../../server/handler.ts';
import stream from 'node:stream/web';
import type { HttpHandler } from '../../http/server/types.ts';


class FailureAfterResponseCompletedError extends Error {
    readonly completedResponse: ClientHttpResponse;

    constructor(response: ClientHttpResponse, options: { cause?: Error } = {}) {
        super(`error occurred after response was completed: ${response}`, options);
        this.completedResponse = response;
    }
}

type Sink<T> = {
    resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: unknown) => void,
    promise: Promise<T>
}

async function sink<T>(): Promise<Sink<T>> {
    try {
        return await new Promise<Sink<T>>((result, _error) => {

            const promise = new Promise<T>((resolve, reject) => {
                Promise.resolve().then(() => {
                    result({promise, resolve, reject});
                });
            });
        });
    }
    catch (error) {
        throw error;
    }
}

export class HttpHandlerConnector implements ClientHttpConnector {
    static readonly #logger = getLogger('test.http.handler.connector');
    readonly #httpHandler: HttpHandler;
    readonly #sslInfo?: SslInfo;

    constructor(httpHandler: HttpHandler, sslInfo?: SslInfo) {
        this.#httpHandler = httpHandler;
        this.#sslInfo = sslInfo;
    }

    async connect(method: HttpMethod, uri: URL,
                  requestCallback: (request: ClientHttpRequest) => Promise<void>): Promise<ClientHttpResponse> {

        const requestWriteSink = await sink<void>();
        const handlerSink = await sink<void>();
        const savedResponse: [ClientHttpResponse | undefined] = [undefined];

        const mockClientRequest = new MockClientHttpRequest(uri, method);
        const mockServerResponse = new MockServerHttpResponse();

        mockClientRequest.writeHandler = async (responseBody) => {
            this.log('Invoking HttpHandler for ', method, uri);
            const mockServerRequest = await this.adoptRequest(mockClientRequest, responseBody);
            const responseToUse = this.prepareResponse(mockServerResponse, mockServerRequest);

            try {
                await this.#httpHandler(mockServerRequest, responseToUse);
                handlerSink.resolve();
            } catch (e) {
                handlerSink.reject(e);
            }
            return true;

        }

        mockServerResponse.writeHandler = async (responseBody) => {
            this.log('Creating client response for ', method, uri);
            savedResponse[0] = this.adoptResponse(mockServerResponse, responseBody);
            return true;
        }
        this.log('Writing client request for ', method, uri);

        requestCallback(mockClientRequest)
            .then(() => {
                requestWriteSink.resolve();
            })
            .catch(error => {
                requestWriteSink.reject(error);
            });

        return Promise.all([requestWriteSink.promise, handlerSink.promise])
            .catch((error) => {
                const response = savedResponse[0];
                throw response !== undefined ? new FailureAfterResponseCompletedError(response, {cause: error}) : error;
            }).then(() => {
                const response = savedResponse[0];
                return response ?? this.adoptResponse(mockServerResponse, stream.ReadableStream.from([]) as ReadableStream<BodyChunk>);
            });

    }

    private log(message: string, method: HttpMethod, uri: URL): void {
        if (HttpHandlerConnector.#logger.enabledFor('debug')) {
            HttpHandlerConnector.#logger.debug(`${message} ${method} "${uri.href}"`);
        }
    }

    private async adoptRequest(request: MockClientHttpRequest, body: ReadableStream<BodyChunk>): Promise<ServerHttpRequest> {
        const method = request.method;
        const uri = request.URL;
        const headers = request.headers;
        const _cookies = request.cookies;
        const result = new MockServerHttpRequest(uri, method)
        for (const name of headers.keys()) {
            let value = headers.get(name);
            if (value !== undefined) {
                if (typeof value === 'number') {
                    value = String(value);
                }
                if (typeof value === 'string') {
                    result.setHeader(name, value);
                } else {
                    for (const v of value) {
                        result.addHeader(name, v);
                    }
                }
            }
        }
        await result.writeBody(body)
        return result;
    }

    private prepareResponse(response: ServerHttpResponse, request: ServerHttpRequest): ServerHttpResponse {
        return request.method === 'HEAD' ? new HttpHeadResponseDecorator(response) : response;

    }

    private adoptResponse(response: MockServerHttpResponse, body: ReadableStream<BodyChunk>): ClientHttpResponse {
        const status = response.statusCode;
        const headers = response.headers;
        const _cookies = response.cookies;
        const result = new MockClientHttpResponse(status);
        for (const name of headers.keys()) {
            let value = headers.get(name);
            if (value !== undefined) {
                if (typeof value === 'number') {
                    value = String(value);
                }
                if (typeof value === 'string') {
                    result.setHeader(name, value);
                } else {
                    for (const v of value) {
                        result.addHeader(name, v);
                    }
                }
            }
        }
        result.body = body;
        return result;
    }
}

